import { useState } from "react";
import { ChevronDown, CornerDownRight, TrendingDown } from "lucide-react";
import {
  Badge,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  cn,
} from "web-design-system";
import { handleError } from "web-core/utils";
import { useUpdatePortfolioReviewLine } from "creso-core/hooks";
import {
  PortfolioReview,
  PortfolioReviewAction,
  PortfolioReviewLine,
} from "creso-core/types";
import { convertToCurrency, formatAsPercentage } from "creso-core/utils";
import { AmcLogo } from "@core/components/logo/amc-logo";
import {
  BUCKET_GROUPS,
  HOLDING_COLUMNS,
  HoldingColumnKey,
} from "./holding-columns";
import { LineCommentPopover } from "./line-comment-popover";

const ACTION_LABEL: Record<PortfolioReviewAction, string> = {
  HOLD: "Hold",
  SWITCH: "Switch",
  EXIT: "Exit",
  ADD: "Add",
};

const SOURCE_BADGE: Record<PortfolioReviewLine["source_label"], string> = {
  Internal: "bg-tag-slate text-tag-slate-foreground",
  External: "bg-tag-orange text-tag-orange-foreground",
  New: "bg-tag-blue text-tag-blue-foreground",
};

const QUARTILE_BADGE: Record<number, string> = {
  1: "bg-tag-green text-tag-green-foreground",
  2: "bg-tag-lime text-tag-lime-foreground",
  3: "bg-tag-yellow text-tag-yellow-foreground",
  4: "bg-tag-red text-tag-red-foreground",
};

export function HoldingsTable({
  review,
  visibleColumns,
}: {
  review: PortfolioReview;
  visibleColumns: HoldingColumnKey[];
}) {
  const columns = HOLDING_COLUMNS.filter((c) => visibleColumns.includes(c.key));
  const readOnly = review.status !== "DRAFT";
  const groups = BUCKET_GROUPS.map((group) => ({
    ...group,
    lines: review.lines
      .filter((line) => line.bucket === group.bucket)
      .sort((a, b) => a.sort_order - b.sort_order),
  })).filter((group) => group.lines.length);

  return (
    <div className="overflow-x-auto rounded-xl border border-border">
      <table className="w-full min-w-200 text-sm">
        <thead className="bg-card">
          <tr>
            <th className="p-4 text-left font-semibold">Fund Name</th>
            {columns.map((column) => (
              <th
                key={column.key}
                className="whitespace-nowrap p-4 text-left font-semibold"
              >
                {column.label}
              </th>
            ))}
            <th className="p-4 text-left font-semibold" colSpan={2}>
              Action
            </th>
          </tr>
        </thead>
        {groups.map((group) => (
          <BucketGroup
            key={group.bucket}
            label={group.label}
            colSpan={columns.length + 3}
          >
            {group.lines.map((line) => (
              <HoldingRow
                key={line.id}
                reviewId={review.id}
                line={line}
                columns={columns.map((c) => c.key)}
                readOnly={readOnly}
              />
            ))}
          </BucketGroup>
        ))}
      </table>
    </div>
  );
}

function BucketGroup({
  label,
  colSpan,
  children,
}: {
  label: string;
  colSpan: number;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(true);
  return (
    <tbody className="border-t border-border">
      <tr>
        <td colSpan={colSpan} className="p-0">
          <button
            type="button"
            aria-expanded={open}
            onClick={() => setOpen(!open)}
            className="flex w-full items-center justify-between p-4 font-semibold"
          >
            {label}
            <ChevronDown
              className={cn(
                "size-4 transition-transform",
                open && "rotate-180"
              )}
            />
          </button>
        </td>
      </tr>
      {open ? children : null}
    </tbody>
  );
}

function HoldingRow({
  reviewId,
  line,
  columns,
  readOnly,
}: {
  reviewId: number;
  line: PortfolioReviewLine;
  columns: HoldingColumnKey[];
  readOnly: boolean;
}) {
  const isNew = line.bucket === "NEW_ADDITION";
  const name = isNew ? line.target_scheme_name : line.source_scheme_name;
  const category = isNew ? line.target_category : line.source_category;
  const quartile = isNew ? line.target_quartile : line.quartile;
  const xirr = line.xirr === null ? null : Number(line.xirr);

  const cells: Record<HoldingColumnKey, React.ReactNode> = {
    source: (
      <Badge
        variant="custom"
        className={cn("font-semibold", SOURCE_BADGE[line.source_label])}
      >
        {line.source_label}
      </Badge>
    ),
    quartile: quartile ? (
      <Badge
        variant="custom"
        className={cn("font-semibold", QUARTILE_BADGE[quartile])}
      >
        Q{quartile}
      </Badge>
    ) : (
      "-"
    ),
    xirr:
      xirr === null ? (
        "-"
      ) : (
        <span className={xirr >= 0 ? "text-constructive" : "text-destructive"}>
          {formatAsPercentage(xirr)}
        </span>
      ),
    invested: isNew ? "-" : convertToCurrency(line.invested_value),
    current: isNew
      ? convertToCurrency(line.amount ?? undefined)
      : convertToCurrency(line.current_value),
    weight: formatAsPercentage(line.weight_pct, "-", 1),
    stcg: convertToCurrency(line.stcg),
    ltcg: convertToCurrency(line.ltcg),
  };

  return (
    <tr className="border-t border-border">
      <td className="px-4 py-3">
        <div className="flex items-center gap-3">
          <AmcLogo amc_code={line.amc_morningstar_code} />
          <div className="flex min-w-0 max-w-70 flex-col gap-1">
            <span className="flex items-center gap-2">
              <span className="truncate">{name}</span>
              {quartile && quartile >= 3 ? (
                <TrendingDown
                  aria-label="Underperforming"
                  className="size-4 shrink-0 text-tag-orange-foreground"
                />
              ) : null}
            </span>
            <span className="truncate text-xs text-muted-foreground">
              {category}
            </span>
            {line.bucket === "SWITCH" && line.target_scheme_name ? (
              <span className="flex items-center gap-1 text-xs text-muted-foreground">
                <CornerDownRight className="size-3 shrink-0" />
                <span className="truncate">{line.target_scheme_name}</span>
              </span>
            ) : null}
          </div>
        </div>
      </td>
      {columns.map((key) => (
        <td key={key} className="whitespace-nowrap px-4 py-3">
          {cells[key]}
        </td>
      ))}
      <td className="px-4 py-3">
        <LineActionSelect reviewId={reviewId} line={line} readOnly={readOnly} />
      </td>
      <td className="py-3 pr-4">
        <LineCommentPopover
          reviewId={reviewId}
          line={line}
          readOnly={readOnly}
        />
      </td>
    </tr>
  );
}

function LineActionSelect({
  reviewId,
  line,
  readOnly,
}: {
  reviewId: number;
  line: PortfolioReviewLine;
  readOnly: boolean;
}) {
  const { mutate: updateLine, isPending } = useUpdatePortfolioReviewLine();
  const isNew = line.bucket === "NEW_ADDITION";
  const options: PortfolioReviewAction[] = isNew
    ? ["ADD"]
    : ["HOLD", "SWITCH", "EXIT"];
  // A MANUAL review starts every line in HOLD with no action chosen yet.
  const value = line.action ?? (isNew ? "ADD" : "HOLD");

  return (
    <Select
      value={value}
      disabled={readOnly || isNew || isPending}
      onValueChange={(action) =>
        updateLine(
          {
            reviewId,
            lineId: line.id,
            action: action as PortfolioReviewAction,
          },
          {
            onError: (error) => handleError(error, "Could not update action"),
          }
        )
      }
    >
      <SelectTrigger className="w-36 bg-background" aria-label="Action">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {options.map((action) => (
          <SelectItem key={action} value={action}>
            {ACTION_LABEL[action]}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
